import React from 'react';
import { api } from '../lib/api';
import { money, today, workflowColour } from '../lib/format';
import { useResource } from '../lib/hooks';
import type { Cost, Payment, Property, Task } from '../lib/types';
import { Loading, Stat, TaskRow, type Ctx } from '../main';
import { StatusTag } from './Properties';

const monthKey = (d: string): string => d.slice(0, 7);

/** The last six calendar months, oldest first, with what came in and went out in each. */
export function cashFlow(payments: Payment[], costs: Cost[], now = today()): Array<{ key: string; label: string; in: number; out: number }> {
  const [y, m] = now.split('-').map(Number) as [number, number];
  return Array.from({ length: 6 }, (_, i) => {
    const d = new Date(Date.UTC(y, m - 1 - (5 - i), 1));
    const key = d.toISOString().slice(0, 7);
    return {
      key,
      label: d.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' }),
      in: payments.filter((p) => monthKey(p.received_on) === key).reduce((a, b) => a + Number(b.amount), 0),
      out: costs.filter((c) => monthKey(c.paid_on) === key).reduce((a, b) => a + Number(b.amount), 0),
    };
  });
}

/**
 * Portfolio health and cash position.
 *
 * Today answers "what needs me now". This answers "how are we doing": the
 * money over six months, occupancy, and which sites carry the overdue work.
 * Every number is computed from what the API returns — nothing is estimated.
 */
export function Dashboard({ ctx, tasks, properties }: {
  ctx: Ctx; tasks: Task[]; properties: Property[];
}): React.JSX.Element {
  const payments = useResource(() => api.payments(), []);
  const costs = useResource(() => api.costs(), []);

  const now = today();
  const month = monthKey(now);
  const rooms = properties.reduce((a, p) => a + p.roomCount, 0);
  const occupied = properties.reduce((a, p) => a + p.occupiedCount, 0);
  const ps = payments.data?.payments ?? [];
  const cs = costs.data?.costs ?? [];
  const inThisMonth = ps.filter((p) => monthKey(p.received_on) === month).reduce((a, b) => a + Number(b.amount), 0);
  const outThisMonth = cs.filter((c) => monthKey(c.paid_on) === month).reduce((a, b) => a + Number(b.amount), 0);
  const dueIn = tasks.filter((t) => t.type === 'Income');
  const late = tasks.filter((t) => t.dueDate < now).sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  const flow = cashFlow(ps, cs, now);
  const peak = Math.max(...flow.map((f) => Math.max(f.in, f.out)), 1);

  const byWorkflow: Record<string, number> = {};
  for (const t of tasks) byWorkflow[t.workflow] = (byWorkflow[t.workflow] ?? 0) + 1;
  const busiest = Math.max(...Object.values(byWorkflow), 1);
  const lateAt = (id: string): number => late.filter((t) => t.propertyId === id).length;

  return (
    <>
      <div className="between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22 }}>Dashboard</h1>
          <div className="sub">Portfolio health, cash position, and where the overdue work sits.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn sm" onClick={() => ctx.go('timeline')}>Open the timeline</button>
          <button className="btn sm accent w" onClick={() => ctx.newTask()}>New task</button>
        </div>
      </div>

      <div className="grid g4">
        <Stat label="Occupancy" value={rooms ? `${Math.round((occupied / rooms) * 100)}%` : '—'} tone="good"
          foot={`${occupied} of ${rooms} rooms across ${properties.length} sites`} />
        <Stat label="Collected this month" value={money(inThisMonth)} foot={plural(ps.filter((p) => monthKey(p.received_on) === month).length, 'payment')} />
        <Stat label="Spent this month" value={money(outThisMonth)} foot={plural(cs.filter((c) => monthKey(c.paid_on) === month).length, 'cost')} />
        <Stat label="Still to collect" value={money(dueIn.reduce((a, b) => a + b.amount, 0))}
          foot={`${dueIn.length} open income task${dueIn.length === 1 ? '' : 's'}`} />
      </div>

      <div className="grid dash2" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="hd"><h3>Cash flow</h3><span className="r sub">Last six months</span></div>
          <div className="bd">
            {(payments.loading || costs.loading) ? <Loading /> : (
              <svg viewBox="0 0 520 200" style={{ width: '100%', height: 200 }} role="img"
                aria-label="Money in and out over the last six months">
                {[0, 0.25, 0.5, 0.75, 1].map((f) => (
                  <g key={f}>
                    <line x1={44} x2={520} y1={170 - f * 150} y2={170 - f * 150} stroke="#EBEFED" />
                    <text x={0} y={174 - f * 150} fontSize={9} fill="#9BADB3">{short(peak * f)}</text>
                  </g>
                ))}
                {flow.map((f, i) => {
                  const x = 62 + i * 76;
                  const hi = (f.in / peak) * 150, ho = (f.out / peak) * 150;
                  return (
                    <g key={f.key}>
                      <rect x={x} y={170 - hi} width={22} height={hi} fill="#0F6B5C" rx={2}><title>{`${f.label}: in ${money(f.in)}`}</title></rect>
                      <rect x={x + 25} y={170 - ho} width={22} height={ho} fill="#AC4034" opacity={0.82} rx={2}><title>{`${f.label}: out ${money(f.out)}`}</title></rect>
                      <text x={x + 23} y={188} fontSize={10} fill="#728A92" textAnchor="middle">{f.label}</text>
                    </g>
                  );
                })}
              </svg>
            )}
            <div className="maplegend sub" style={{ padding: 0 }}>
              <span><i style={{ background: '#0F6B5C' }} />Money in</span>
              <span><i style={{ background: '#AC4034' }} />Money out</span>
              <span style={{ marginLeft: 'auto' }}>
                Six-month net {money(flow.reduce((a, f) => a + f.in - f.out, 0))}
              </span>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="hd"><h3>Open work by workflow</h3></div>
          <div className="bd">
            {Object.entries(byWorkflow).sort((a, b) => b[1] - a[1]).map(([workflow, count]) => (
              <div key={workflow} style={{ marginBottom: 11 }}>
                <div className="between" style={{ fontSize: 12.5 }}>
                  <span style={{ color: workflowColour(workflow), fontWeight: 600 }}>{workflow}</span>
                  <b className="mono">{count}</b>
                </div>
                <div className="wfbar"><i style={{ width: `${(count / busiest) * 100}%`, background: workflowColour(workflow) }} /></div>
              </div>
            ))}
            {tasks.length === 0 && <div className="sub">Nothing open.</div>}
          </div>
        </div>
      </div>

      <div className="grid g2" style={{ marginTop: 16 }}>
        <div className="card">
          <div className="hd"><h3>Overdue</h3>
            <span className="r"><span className="tag t-bad">{late.length}</span>
              <button className="btn sm" onClick={() => ctx.go('tasks')}>All tasks</button></span>
          </div>
          <div className="bd">
            {late.slice(0, 6).map((t) => <TaskRow key={t.id} task={t} ctx={ctx} />)}
            {late.length === 0 && <div className="note ok">Every open task is inside its due date.</div>}
          </div>
        </div>
        <div className="card">
          <div className="hd"><h3>Portfolio</h3>
            <span className="r"><button className="btn sm" onClick={() => ctx.go('properties')}>Manage</button></span>
          </div>
          <table className="responsive">
            <tbody>{[...properties].sort((a, b) => lateAt(b.id) - lateAt(a.id)).slice(0, 8).map((p) => (
              <tr key={p.id}>
                <td data-label="Property"><b>{p.name}</b><div className="sub">{p.district || p.city}</div></td>
                <td data-label="Status"><StatusTag status={p.status} /></td>
                <td data-label="Let" style={{ width: 150 }}>
                  {p.roomCount ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="wfbar" style={{ flex: 1, marginTop: 0 }}>
                        <i style={{ width: `${(p.occupiedCount / p.roomCount) * 100}%`,
                          background: p.occupiedCount === p.roomCount ? 'var(--teal)' : 'var(--accent)' }} />
                      </span>
                      <span className="mono sub">{p.occupiedCount}/{p.roomCount}</span>
                    </span>
                  ) : <span className="sub">no rooms yet</span>}
                </td>
                <td data-label="Late" className={lateAt(p.id) ? 'late mono' : 'mono sub'}>{lateAt(p.id) || '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const short = (n: number): string =>
  n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}m`
    : n >= 10_000 ? `${Math.round(n / 1000)}k`
      : n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(Math.round(n));

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;
